import { apiClient } from './client.js'

// ==================== SHIFT MANAGEMENT API ====================

/**
 * Open a new shift
 * @param {Object} data - Shift opening data
 * @param {number} data.opening_cash - Opening cash amount in IDR
 * @param {string} data.notes - Opening notes (optional)
 */
export const openShift = async (data) => {
  return apiClient.post('/shifts', data)
}

/**
 * Get list of shifts with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Records per page (default: 20, max: 100)
 */
export const getShifts = async (params = {}) => {
  const query = new URLSearchParams()
  if (params.page) query.append('page', params.page)
  if (params.limit) query.append('limit', params.limit)
  
  return apiClient.get(`/shifts?${query.toString()}`)
}

/**
 * Get active shift (status: OPEN)
 * Returns the currently open shift for the tenant
 */
export const getActiveShift = async () => {
  return apiClient.get('/shifts/active')
}

/**
 * Get shift detail by ID with all cash entries
 * @param {string} shiftId - Shift ID (UUID)
 */
export const getShiftDetail = async (shiftId) => {
  return apiClient.get(`/shifts/${shiftId}`)
}

/**
 * Close a shift and calculate recap automatically
 * @param {string} shiftId - Shift ID to close
 * @param {Object} data - Closing data
 * @param {number} data.closing_cash - Actual cash amount when closing (physical count)
 * @param {string} data.notes - Closing notes (optional)
 */
export const closeShift = async (shiftId, data) => {
  return apiClient.post(`/shifts/${shiftId}/close`, data)
}

/**
 * Add cash entry to active shift (CASH_IN or CASH_OUT)
 * @param {string} shiftId - Shift ID
 * @param {Object} entryData - Cash entry data
 * @param {string} entryData.type - 'CASH_IN' or 'CASH_OUT'
 * @param {number} entryData.amount - Amount (must be > 0, IDR)
 * @param {string} entryData.description - Description of cash entry (optional)
 */
export const addCashEntry = async (shiftId, entryData) => {
  return apiClient.post(`/shifts/${shiftId}/cash-entries`, entryData)
}

/**
 * Get all cash entries for a specific shift
 * @param {string} shiftId - Shift ID
 */
export const getCashEntries = async (shiftId) => {
  return apiClient.get(`/shifts/${shiftId}/cash-entries`)
}

/**
 * Get orders (transactions) that were recorded during a specific shift
 * @param {string} shiftId - Shift ID (UUID)
 * @param {Object} params - Query parameters
 * @param {number} params.page   - Page number (default: 1)
 * @param {number} params.limit  - Records per page (default: 20, max: 100)
 * @param {string} params.status - Filter by order status: DONE | CANCELLED | PENDING
 * @param {string} params.query  - Search by transaction number or customer name
 */
export const getShiftOrders = async (shiftId, params = {}) => {
  const query = new URLSearchParams()
  if (params.page)  query.append('page',  params.page)
  if (params.limit) query.append('limit', params.limit)
  if (params.query) query.append('query', params.query)
  // status can be a single string or an array (send as CSV: A,B)
  if (params.status) {
    const statusValue = Array.isArray(params.status)
      ? params.status.filter(Boolean).join(',')
      : params.status
    if (statusValue) query.append('status', statusValue)
  }
  const qs = query.toString()
  return apiClient.get(`/shifts/${shiftId}/orders${qs ? `?${qs}` : ''}`)
}

/**
 * Get summary/recap for a specific shift
 * @param {string} shiftId - Shift ID (UUID)
 */
export const getShiftSummary = async (shiftId) => {
  return apiClient.get(`/shifts/${shiftId}/summary`)
}

// ==================== SHIFT UTILITIES ====================

/**
 * Calculate expected cash for a shift
 * @param {Object} shift - Shift data
 * @returns {number} Expected cash amount
 */
export const calculateExpectedCash = (shift) => {
  const { 
    opening_cash = 0, 
    total_cash_sales = 0, 
    total_cash_in = 0, 
    total_cash_out = 0 
  } = shift
  
  return opening_cash + total_cash_sales + total_cash_in - total_cash_out
}

/**
 * Calculate cash difference
 * @param {number} closingCash - Actual closing cash
 * @param {number} expectedCash - Expected cash amount
 * @returns {number} Cash difference (positive = surplus, negative = deficit)
 */
export const calculateCashDifference = (closingCash, expectedCash) => {
  return closingCash - expectedCash
}

/**
 * Format shift number for display
 * @param {string} shiftNumber - Shift number (e.g., SHIFT-2026-02-26-0001)
 * @returns {string} Formatted shift number
 */
export const formatShiftNumber = (shiftNumber) => {
  if (!shiftNumber) return ''
  
  // Extract date part and sequence
  const parts = shiftNumber.split('-')
  if (parts.length >= 4) {
    const date = `${parts[1]}-${parts[2]}-${parts[3]}`
    const sequence = parts[4] || '0001'
    return `SHIFT #${sequence} (${date})`
  }
  
  return shiftNumber
}

/**
 * Get shift status color for UI
 * @param {string} status - Shift status ('OPEN' or 'CLOSED')
 * @returns {Object} Status with label and color class
 */
export const getShiftStatusInfo = (status) => {
  const statusConfig = {
    'OPEN': { 
      label: 'Buka', 
      color: 'bg-green-100 text-green-800',
      icon: 'unlock'
    },
    'CLOSED': { 
      label: 'Tutup', 
      color: 'bg-gray-100 text-gray-800',
      icon: 'lock'
    }
  }
  
  return statusConfig[status] || { 
    label: status, 
    color: 'bg-gray-100 text-gray-800',
    icon: 'help'
  }
}

/**
 * Format cash entry type for display
 * @param {string} type - Cash entry type ('CASH_IN' or 'CASH_OUT')
 * @returns {Object} Type info with label and color
 */
export const getCashEntryTypeInfo = (type) => {
  const typeConfig = {
    'CASH_IN': {
      label: 'Kas Masuk',
      color: 'bg-green-100 text-green-800',
      symbol: '+'
    },
    'CASH_OUT': {
      label: 'Kas Keluar', 
      color: 'bg-red-100 text-red-800',
      symbol: '-'
    }
  }
  
  return typeConfig[type] || {
    label: type,
    color: 'bg-gray-100 text-gray-800',
    symbol: ''
  }
}

/**
 * Validate shift opening data
 * @param {Object} data - Opening shift data
 * @returns {Object} Validation result
 */
export const validateShiftOpening = (data) => {
  const errors = []
  
  if (!data.opening_cash && data.opening_cash !== 0) {
    errors.push('Jumlah kas awal harus diisi')
  }
  
  if (data.opening_cash < 0) {
    errors.push('Kas awal tidak boleh negatif')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validate shift closing data
 * @param {Object} data - Closing shift data
 * @returns {Object} Validation result
 */
export const validateShiftClosing = (data) => {
  const errors = []
  
  if (!data.closing_cash && data.closing_cash !== 0) {
    errors.push('Jumlah kas tutup harus diisi')
  }
  
  if (data.closing_cash < 0) {
    errors.push('Kas tutup tidak boleh negatif')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validate cash entry data
 * @param {Object} data - Cash entry data
 * @returns {Object} Validation result
 */
export const validateCashEntry = (data) => {
  const errors = []
  
  if (!data.type || !['CASH_IN', 'CASH_OUT'].includes(data.type)) {
    errors.push('Tipe kas masuk/keluar harus dipilih')
  }
  
  if (!data.amount || data.amount <= 0) {
    errors.push('Jumlah harus lebih dari 0')
  }
  
  if (!data.description || data.description.trim() === '') {
    errors.push('Keterangan harus diisi')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Format duration between shift open and close
 * @param {string} openedAt - Opening timestamp
 * @param {string} closedAt - Closing timestamp (optional)
 * @returns {string} Formatted duration
 */
export const formatShiftDuration = (openedAt, closedAt = null) => {
  const start = new Date(openedAt)
  const end = closedAt ? new Date(closedAt) : new Date()
  
  const diffMs = end - start
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  
  if (diffHours > 0) {
    return `${diffHours} jam ${diffMinutes} menit`
  }
  
  return `${diffMinutes} menit`
}

/**
 * Calculate grand total for shift
 * @param {Object} shift - Shift data
 * @returns {number} Grand total (cash + non-cash sales)
 */
export const calculateShiftGrandTotal = (shift) => {
  const { total_cash_sales = 0, total_non_cash_sales = 0 } = shift
  return total_cash_sales + total_non_cash_sales
}